import type { Hash } from 'viem'

import type {
  TransactionGasEstimateError,
  TransactionGasLimitSource,
  VaultExitType
} from '@/app/api/t/constants'
import type { WalletType } from '@/lib/observability/shared/wallet-type'
import type { TxSource, VariantFeatureFlagKey } from '@/lib/shared/utils/flags.constants'

import {
  SimulationError,
  type SimulationFailureType,
  type SimulationRevertCategory
} from '@/services/simulate/simulate'

import { sendMetricBeacon } from '../../utils/metric-beacon'
import {
  classifyAndTransformOutcome,
  type TransactionErrorCategory,
  type TransactionOutcome
} from './error-classifier'
import { summarizeTransactionError } from './error-summary'

interface TransactionMetricPayload {
  chainId: number
  actionType?: string
  flowActionType?: string
  outcome: TransactionOutcome
  txSource: TxSource
  flow?: VariantFeatureFlagKey
  txFlag?: TxSource
  exitType?: VaultExitType
  txHash?: Hash
  walletType: WalletType
  walletConnector?: string
  sdkAction?: string
  vaultAddress?: string
  errorCategory?: TransactionErrorCategory
  errorMessage?: string
  errorName?: string
  errorCode?: string
  errorDetails?: string
  simulateCode?: string
  simulateFailureType?: SimulationFailureType
  simulateHttpStatus?: number
  simulateReason?: string
  simulateRevertCategory?: SimulationRevertCategory
  simulateTxCount?: number
  gasEstimate?: number
  gasLimit?: number
  /** Gas limit of the mined tx from eth_getTransactionByHash; differs from gasLimit when the wallet replaced it */
  gasLimitOnchain?: number
  gasUsed?: number
  gasLimitSource?: TransactionGasLimitSource
  gasEstimateError?: TransactionGasEstimateError
  gasReceiptReplaced?: boolean
  reviewSessionId?: string
  attemptId?: string
}

const SIMULATE_CODE_MAX_LENGTH = 100
const SIMULATE_REASON_MAX_LENGTH = 200

function sanitizeSimulationMetadata(value: string, maxLength: number): string | undefined {
  const cleaned = Array.from(value, char => {
    const code = char.codePointAt(0) ?? 0
    return code < 0x20 || (code >= 0x7f && code <= 0x9f) ? ' ' : char
  })
    .join('')
    .replace(/\s+/g, ' ')
    .trim()

  if (!cleaned) return undefined

  return cleaned.length > maxLength ? `${cleaned.slice(0, maxLength - 3)}...` : cleaned
}

function getSimulationMetadata(
  error: unknown
): Pick<
  TransactionMetricPayload,
  | 'simulateCode'
  | 'simulateFailureType'
  | 'simulateHttpStatus'
  | 'simulateReason'
  | 'simulateRevertCategory'
  | 'simulateTxCount'
> {
  if (!(error instanceof SimulationError)) return {}

  return {
    simulateCode:
      sanitizeSimulationMetadata(error.details?.code ?? '', SIMULATE_CODE_MAX_LENGTH) ??
      'MISSING_CODE',
    simulateFailureType: error.failureType,
    simulateHttpStatus: error.details?.httpStatus,
    simulateReason: error.details?.reason
      ? sanitizeSimulationMetadata(error.details.reason, SIMULATE_REASON_MAX_LENGTH)
      : undefined,
    simulateRevertCategory:
      error.details?.code === 'SIMULATION_REVERTED'
        ? (error.revertCategory ?? 'unknown')
        : error.revertCategory,
    simulateTxCount: error.transactions.length
  }
}

// `/api/t/transaction` backs the BetterStack tx success-rate dashboard.
// A retry keeps the same reviewSessionId but gets a new attemptId, so count only
// the first failure per review session *and step*. Keying on actionType too means
// a retry storm on one step is deduped, while a later, distinct step failure
// (e.g. a bundle revert after a retried approval succeeds) is still counted.
const countedFailureKeys = new Set<string>()
const MAX_COUNTED_FAILURE_KEYS = 500

// Peer names are resolved asynchronously, so dedup on the connector id only.
const walletConnectorId = (connector?: string) => connector?.split(':', 1)[0] ?? ''

function rememberFailure(key: string): boolean {
  if (countedFailureKeys.has(key)) return false

  if (countedFailureKeys.size >= MAX_COUNTED_FAILURE_KEYS) {
    const oldestKey = countedFailureKeys.values().next().value
    if (oldestKey) countedFailureKeys.delete(oldestKey)
  }

  countedFailureKeys.add(key)
  return true
}

function shouldSendTransactionMetric(payload: TransactionMetricPayload): boolean {
  if (payload.outcome !== 'failure' || !payload.reviewSessionId) return true
  return rememberFailure(
    `${payload.reviewSessionId}::${payload.actionType ?? ''}::${walletConnectorId(payload.walletConnector)}`
  )
}

export interface TrackTransactionParams {
  chainId: number
  actionType?: string
  /** Action type of the user flow this step belongs to (e.g. `VaultV2_Deposit` for its `approval_tx`); defaults to `actionType` */
  flowActionType?: string
  outcome: 'success' | 'failure' | 'receipt_timeout_pending'
  /** Which path sent the transaction, so a flag split can be compared and rolled back per source */
  txSource: TxSource
  /** The tx* feature-flag key that selected this transaction flow. */
  flow?: VariantFeatureFlagKey
  /** The resolved value of the flow's tx* feature flag. */
  txFlag?: TxSource
  /** Which exit a vault withdraw-family tx took; absent on other operations */
  exitType?: VaultExitType
  /** Mined or broadcast hash; absent for rejections and unexecuted Safe proposals */
  txHash?: Hash
  error?: unknown
  walletType?: WalletType
  /** wagmi connector id (e.g. io.rabby, io.metamask, xyz.tempo) — names the wallet behind walletType */
  walletConnector?: string
  sdkAction?: string
  vaultAddress?: string
  /** Stable across retries of the same review session — dedupes retry failures in stats */
  reviewSessionId?: string
  /** Unique per submit attempt — joins with the PostHog checkout funnel */
  attemptId?: string
  gasEstimate?: number
  gasLimit?: number
  gasUsed?: number
  gasLimitSource?: TransactionGasLimitSource
  /** Why the estimate failed, when gasLimitSource is 'estimate_failed' */
  gasEstimateError?: TransactionGasEstimateError
  /** True when the confirmed receipt was a cancel/replace, so its gasUsed is excluded from stats */
  gasReceiptReplaced?: boolean
}

export function trackTransaction(
  params: TrackTransactionParams,
  /**
   * Dedupe is claimed before waiting; the event is sent without the lookup if the page hides,
   * freezes or closes first. The promise must not reject.
   */
  pendingGasLimitOnchain?: Promise<number | undefined>
): void {
  const {
    chainId,
    actionType,
    flowActionType,
    outcome,
    txSource,
    flow,
    txFlag,
    exitType,
    txHash,
    error,
    walletType,
    walletConnector,
    sdkAction,
    vaultAddress,
    reviewSessionId,
    attemptId,
    gasEstimate,
    gasLimit,
    gasUsed,
    gasLimitSource,
    gasEstimateError,
    gasReceiptReplaced
  } = params

  const { outcome: finalOutcome, errorCategory } = classifyAndTransformOutcome(outcome, error)
  const simulationMetadata =
    finalOutcome !== 'failure'
      ? {}
      : errorCategory === 'simulation_failed' && !(error instanceof SimulationError)
        ? {
            // Message-only `simulation_failed` (e.g. wallet gas estimate or bundler revert text),
            // not an `/api/simulate` failure.
            simulateCode: 'MESSAGE_CLASSIFIED'
          }
        : getSimulationMetadata(error)
  const errorSummary = finalOutcome === 'failure' ? summarizeTransactionError(error) : undefined
  const errorMetadata = errorSummary
    ? {
        errorMessage: errorSummary.message,
        errorName: errorSummary.name,
        errorCode: errorSummary.code,
        errorDetails: errorSummary.details
      }
    : {}

  const payload: TransactionMetricPayload = {
    chainId,
    actionType,
    flowActionType: flowActionType ?? actionType,
    outcome: finalOutcome,
    txSource,
    flow,
    txFlag,
    exitType,
    txHash,
    walletType: walletType ?? 'eoa',
    walletConnector,
    sdkAction,
    vaultAddress,
    errorCategory,
    ...errorMetadata,
    ...simulationMetadata,
    gasEstimate,
    gasLimit,
    gasUsed,
    gasLimitSource,
    gasEstimateError,
    gasReceiptReplaced,
    reviewSessionId,
    attemptId
  }

  if (!shouldSendTransactionMetric(payload)) return

  if (!pendingGasLimitOnchain) {
    sendMetricBeacon('/api/t/transaction', payload)
    return
  }

  let sent = false

  function send(gasLimitOnchain?: number): void {
    if (sent) return
    sent = true
    if (typeof window !== 'undefined') window.removeEventListener('pagehide', flush)
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', flushIfHidden)
      document.removeEventListener('freeze', flush)
    }
    sendMetricBeacon('/api/t/transaction', { ...payload, gasLimitOnchain })
  }

  function flush(): void {
    send()
  }

  function flushIfHidden(): void {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') send()
  }

  if (typeof window !== 'undefined') window.addEventListener('pagehide', flush)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', flushIfHidden)
    document.addEventListener('freeze', flush)
  }
  void pendingGasLimitOnchain.then(send)
}
